import classNames from 'classnames';
import React from 'react';
import { FiPlusCircle } from 'react-icons/fi';

import SkillPill from './SkillPill';

import type { GlobalRatingItem, MappedSkillRating, OverlaySkill, SkillTreeNodeBase } from '../types';

type Props<TSkill extends SkillTreeNodeBase<TSkill>> = {
  skill: TSkill;
  parentData: TSkill[];
  openAddSkillModal: (parentData: TSkill[]) => void;
  openEditSkillModal: (skill: TSkill, parentData: TSkill[]) => void;
  showAddSkills?: boolean;
  globalRatings?: GlobalRatingItem[];
  mappedSkillRating?: MappedSkillRating;
  selectedSkills?: TSkill[] | null;
  onSelectedSkillsChange?: (skills: TSkill[]) => void;
  setIsOverlayOpenFor?: (skill: OverlaySkill<TSkill> | null) => void;
  isOpenFor?: OverlaySkill<TSkill> | number | string | null;
  isSelf?: boolean;
  isTeamManager?: boolean;
  ratingOpenFor: TSkill | number | string | null;
  setRatingOpenFor: React.Dispatch<React.SetStateAction<TSkill | number | string | null>>;
};

const LastSkillParent = <TSkill extends SkillTreeNodeBase<TSkill>>({
  skill,
  parentData,
  openAddSkillModal,
  openEditSkillModal,
  showAddSkills,
  globalRatings,
  mappedSkillRating,
  selectedSkills,
  onSelectedSkillsChange,
  setIsOverlayOpenFor,
  isOpenFor,
  isSelf,
  isTeamManager,
  ratingOpenFor,
  setRatingOpenFor,
}: Props<TSkill>) => {
  const { name, subSkills, totalSkills } = skill;
  const subSkillParentData = [...parentData, skill];

  const handlePillClick = (selectedSkill: TSkill, selectedParentData: TSkill[]) => {
    if (!mappedSkillRating) {
      if (setIsOverlayOpenFor && ((isSelf && selectedSkill.skillStatus) || isTeamManager)) {
        setIsOverlayOpenFor({ ...selectedSkill, parentData: selectedParentData } as OverlaySkill<TSkill>);
      }
    }

    if (showAddSkills) {
      openEditSkillModal(selectedSkill, selectedParentData);
    }

    if (!showAddSkills) {
      setRatingOpenFor((previousState) => {
        const previousStateId =
          typeof previousState === 'object' && previousState !== null ? previousState.id : previousState;

        if (previousStateId === selectedSkill.id) {
          return null;
        }

        return selectedSkill;
      });
    }
  };

  if (!subSkills.length) {
    return (
      <SkillPill
        skill={skill}
        onClick={() => handlePillClick(skill, parentData)}
        globalRatings={globalRatings}
        mappedSkillRating={mappedSkillRating}
        selectedSkills={selectedSkills}
        onSelectedSkillsChange={onSelectedSkillsChange}
        setIsOverlayOpenFor={setIsOverlayOpenFor}
        isOpenFor={isOpenFor}
        isSelf={isSelf}
        isTeamManager={isTeamManager}
        ratingOpenFor={ratingOpenFor}
        setRatingOpenFor={setRatingOpenFor}
      />
    );
  }

  return (
    <div className="last-skill-parent">
      <div className="last-skill-parent__left-container">
        <div
          className={classNames('name', {
            'name--clickable': showAddSkills,
          })}
          onClick={() => showAddSkills && openEditSkillModal(skill, parentData)}
        >
          {name ?? ''}
        </div>
        <div className="count">
          {subSkills.length}
          {totalSkills && `/${totalSkills}`} skills
        </div>
      </div>

      <div className="last-skill-parent__right-container">
        {subSkills.map((lastSkill) => (
          <SkillPill
            key={String(lastSkill.id)}
            skill={lastSkill}
            onClick={() => handlePillClick(lastSkill, subSkillParentData)}
            globalRatings={globalRatings}
            mappedSkillRating={mappedSkillRating}
            selectedSkills={selectedSkills}
            onSelectedSkillsChange={onSelectedSkillsChange}
            setIsOverlayOpenFor={setIsOverlayOpenFor}
            isOpenFor={isOpenFor}
            isSelf={isSelf}
            isTeamManager={isTeamManager}
            ratingOpenFor={ratingOpenFor}
            setRatingOpenFor={setRatingOpenFor}
          />
        ))}

        <div className="add-skill">
          {showAddSkills && (
            <button onClick={() => openAddSkillModal(subSkillParentData)}>
              <FiPlusCircle className="add-skill__icon" color="#29B6F6" size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LastSkillParent;
